import { useId } from 'react'
import { observer } from 'mobx-react-lite'
import type { BusinessTaxRefundMethodSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/refund-methods/BusinessTaxRefundMethodSupportingItemViewModel'
import type { TaxInfoGatheringViewModel } from '@/features/jobs/details/tax-type/info-gathering/TaxInfoGatheringViewModel'
import { TaxInfoGatheringWizardContentButtonGroupContainer } from '@/features/jobs/details/tax-type/info-gathering/components/TaxInfoGatheringWizardContentButtonGroupContainer'
import { WizardContentBody, WizardContentFooter } from '@/components/Wizard'
import { TaxRefundMethodEditor } from '@/features/tax-profile/tax-refund-methods/components/TaxRefundMethodEditor'
import { TaxRefundMethodCardListSectionsContainer } from '@/features/jobs/details/tax-type/info-gathering/refund-methods/components/ProvidedTaxRefundMethodCardList/TaxRefundMethodCardListSectionsContainer'

export interface BusinessTaxRefundMethodWizardContentContainerProps {
  itemVm: BusinessTaxRefundMethodSupportingItemViewModel
  taxInfoGatheringVm: TaxInfoGatheringViewModel
}

/**
 * Business tax refund method wizard content container that displays different
 * layouts based on whether the user has existing methods or not.
 */
export const BusinessTaxRefundMethodWizardContentContainer = observer(
  function BusinessTaxRefundMethodWizardContentContainer({
    itemVm,
    taxInfoGatheringVm,
  }: BusinessTaxRefundMethodWizardContentContainerProps) {
    const formId = useId()
    const isSkeleton = itemVm.isFetchingEditorData
    const isCompleteInfoGatheringPending =
      taxInfoGatheringVm.confirmCompleteInfoGathering.pending
    const hasNoExistingMethods =
      itemVm.otherAvailableMethods.length === 0 && !itemVm.method

    // When user has no existing methods, show the inline form
    if (hasNoExistingMethods && !itemVm.isReadOnly) {
      return (
        <>
          <WizardContentBody>
            <TaxRefundMethodEditor
              formId={formId}
              isDisabled={
                itemVm.taxRefundMethodEditorVm.save.pending ||
                isCompleteInfoGatheringPending
              }
              isSkeleton={isSkeleton}
              vm={itemVm.taxRefundMethodEditorVm}
            />
          </WizardContentBody>

          <WizardContentFooter>
            <TaxInfoGatheringWizardContentButtonGroupContainer
              isSkeleton={isSkeleton}
              variant={
                // Don't show the save button until we've chosen a type.
                itemVm.taxRefundMethodEditorVm.methodType ? 'save' : 'nav-only'
              }
              formId={formId}
              isSavePending={itemVm.taxRefundMethodEditorVm.save.pending}
              taxInfoGatheringVm={taxInfoGatheringVm}
              itemVm={itemVm}
            />
          </WizardContentFooter>
        </>
      )
    }

    // When user has existing methods, show the regular container with nav-only buttons
    return (
      <>
        <WizardContentBody>
          <TaxRefundMethodCardListSectionsContainer
            vm={itemVm}
            isDisabled={isCompleteInfoGatheringPending}
          />
        </WizardContentBody>

        <WizardContentFooter>
          <TaxInfoGatheringWizardContentButtonGroupContainer
            isSkeleton={isSkeleton}
            variant="nav-only"
            taxInfoGatheringVm={taxInfoGatheringVm}
            itemVm={itemVm}
          />
        </WizardContentFooter>
      </>
    )
  },
)
